/**
 * A labelled block of copyable text (Feature: terraform-provider, task 10.2; Requirements 14.1, 14.2,
 * 14.6).
 *
 * Every Terraform snippet the dashboard hands a reader — the `terraform import` command, the resource
 * stanza, the provider configuration — is text whose only purpose is to be pasted somewhere else, so the
 * copy control is the interaction and the rendering is secondary.
 *
 * The copy control tells the truth about whether it copied, for the reason
 * {@link import('../components/ApiKeyRevealDialog').ApiKeyRevealDialog} spells out at length:
 * `navigator.clipboard` is absent on a non-secure origin and in some hardened browser configurations,
 * and an optional-chained call there resolves happily having copied nothing. A reader who trusts a false
 * "Copied" pastes an empty buffer into their Terraform configuration and gets a confusing error a long
 * way from its cause. So an unavailable or refused clipboard says so, and selects the text instead —
 * the fallback a keyboard user can finish by hand.
 *
 * The stakes are lower here than for a one-time API_Key_Value (a stanza can always be re-read from this
 * page), which is why this is a plain block rather than a dialog. The honesty is the same either way.
 */

import { useRef, useState } from 'react';

/** Whether the last copy attempt worked, and if not, why not. */
type CopyState = 'idle' | 'copied' | 'unavailable' | 'failed';

const COPY_MESSAGES: Record<Exclude<CopyState, 'idle'>, string> = {
  copied: 'Copied to the clipboard.',
  unavailable:
    'This browser will not give the page clipboard access. The text is selected — copy it manually.',
  failed: 'The copy was refused. The text is selected — copy it manually.',
};

export interface CopyableSnippetProps {
  /** What this snippet is, shown above it and used as the copy button's accessible name. */
  readonly label: string;
  /** The exact text to render and to copy. Rendered verbatim, whitespace included. */
  readonly text: string;
  /** Test hook on the `<pre>`, so a test can assert the rendered text byte-for-byte. */
  readonly testId?: string;
}

export function CopyableSnippet({ label, text, testId }: CopyableSnippetProps): JSX.Element {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const block = useRef<HTMLPreElement>(null);

  /**
   * Select the rendered text so a reader can copy it by hand.
   *
   * Wrapped in a `try`, because the Selection API is optional in a way that matters: jsdom implements
   * only part of it, and a headless or embedded browser may implement none. A failed *fallback* must not
   * throw over the top of the message explaining that the fallback is what is left.
   */
  function selectText(): void {
    const node = block.current;
    if (node === null) return;
    try {
      const selection = window.getSelection();
      if (selection === null) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      // Nothing to recover: the message below already tells the reader to copy manually.
    }
  }

  async function copy(): Promise<void> {
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined) {
      setCopyState('unavailable');
      selectText();
      return;
    }
    try {
      await clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
      selectText();
    }
  }

  return (
    <div className="snippet">
      <div className="snippet-header">
        <span className="snippet-label">{label}</span>
        <button type="button" className="secondary" aria-label={`Copy ${label}`} onClick={() => void copy()}>
          Copy
        </button>
      </div>
      <pre ref={block} data-testid={testId}>
        <code>{text}</code>
      </pre>
      {copyState === 'idle' ? null : (
        <p role="status" className="muted snippet-status">
          {COPY_MESSAGES[copyState]}
        </p>
      )}
    </div>
  );
}
